import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cx } from 'linaria';
import { Player } from '@lottiefiles/react-lottie-player';
import { RegularButton, Typography } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import Cookies from 'universal-cookie';
import { State } from 'store/types';
import { useBodyScrollLock } from 'common/hooks';
import { getHostname } from 'utils';
import { getIsNewTheme } from 'selectors';
import {
  mediaStyle,
  introContainerStyle,
  notificationContainer,
  notificationTextBlock,
  notificationDescription,
  notificationCloseButton,
  mediaContainer,
  notificationHeading,
  maxHeight,
  opacityInvisible,
  animationZIndex,
  smallHeightOverflowYScroll,
  smallHeightAdditionalMargin,
} from './styles';
import {
  CHANGE_POSITION_TIME,
  DATA_QA_CONTINUE_BUTTON,
  DATA_QA_INTRO_CONTAINER,
  DELAY_TIME,
  IS_LEMANA_PRO_ANIMATION_SHOWN_COOKIE,
  MODAL_CLOSE_TIME_DELAY,
  ONE_YEAR,
  SHOW_HINT_TIME,
} from './constants';

export const IntroAnimation = () => {
  const { cookies, logoProUrl } = useStoreon<State>('cookies', 'logoProUrl');
  const isNewTheme = getIsNewTheme(cookies);
  const clientCookies = useMemo(() => new Cookies(), []);
  const cookieExist = clientCookies.get(IS_LEMANA_PRO_ANIMATION_SHOWN_COOKIE) !== undefined;
  const [isModalAlreadyDisplayed, setIsModalAlreadyDisplayed] = useState(
    cookies && cookies[IS_LEMANA_PRO_ANIMATION_SHOWN_COOKIE] === 'true'
  );

  const [isPositionChanged, setIsPositionChanged] = React.useState(false);
  const [isHintShown, setIsHintShown] = useState(false);
  const [modalContainerStyle, setModalContainerStyle] = useState<string | undefined>(introContainerStyle);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const isLocked = (!isModalAlreadyDisplayed || !cookieExist) && isNewTheme;

  useBodyScrollLock({ ref: modalContainerRef, locked: isLocked });

  useEffect(() => {
    let changePositionHandler: NodeJS.Timeout;
    let showHintHandler: NodeJS.Timeout;

    if (isNewTheme) {
      changePositionHandler = setTimeout(() => {
        setIsPositionChanged(true);
      }, CHANGE_POSITION_TIME);

      showHintHandler = setTimeout(() => {
        setIsHintShown(true);
      }, SHOW_HINT_TIME);
    }

    return () => {
      clearTimeout(changePositionHandler);
      clearTimeout(showHintHandler);
    };
  }, [isNewTheme]);

  let closeModalHandler = useRef(0);
  let onModalAutoCloseHandler = useRef(0);

  const onModalCloseHandler = useCallback(
    (timeout: number) => {
      if (isNewTheme && !isModalAlreadyDisplayed) {
        const onModalClose = () => {
          setModalContainerStyle(cx(introContainerStyle, opacityInvisible));
          ((closeModalHandler.current as unknown) as NodeJS.Timeout) = setTimeout(() => {
            setIsModalAlreadyDisplayed(true);
          }, DELAY_TIME);

          const hostname = getHostname(window.location.host);

          const cookieOptions = {
            path: '/',
            maxAge: ONE_YEAR,
            domain: `.${hostname}`,
          };
          clientCookies.set(IS_LEMANA_PRO_ANIMATION_SHOWN_COOKIE, true, cookieOptions);
          if (!timeout) {
            clearTimeout(onModalAutoCloseHandler.current);
          }
        };
        if (!timeout) {
          onModalClose();
        } else {
          ((onModalAutoCloseHandler.current as unknown) as NodeJS.Timeout) = setTimeout(() => {
            onModalClose();
          }, timeout);
        }
      }

      return () => {
        clearTimeout(closeModalHandler.current);
        clearTimeout(onModalAutoCloseHandler.current);
      };
    },
    [clientCookies, isModalAlreadyDisplayed, isNewTheme]
  );

  if (isModalAlreadyDisplayed || !isNewTheme) return null;

  return (
    <div
      data-qa={DATA_QA_INTRO_CONTAINER}
      className={
        isPositionChanged ? cx(modalContainerStyle, smallHeightOverflowYScroll) : modalContainerStyle
      }
      ref={modalContainerRef}>
      <div className={isPositionChanged ? mediaContainer : maxHeight}>
        <div className={cx(mediaStyle, animationZIndex)} data-qa="lottie-player-wrap">
          <Player
            autoplay
            loop={false}
            keepLastFrame
            src={logoProUrl || ''}
            onEvent={event => {
              if (event === 'complete') onModalCloseHandler(MODAL_CLOSE_TIME_DELAY);
            }}
          />
        </div>
      </div>

      <div className={isHintShown ? notificationContainer : cx(notificationContainer, opacityInvisible)}>
        <div className={notificationTextBlock}>
          <h3 className={notificationHeading}>Леруа Мерлен теперь Лемана&nbsp;ПРО</h3>
          <Typography className={notificationDescription} color="text-primary" size="body_long" variant="m">
            Продолжаем работать для&nbsp;вас&nbsp;в&nbsp;новом&nbsp;образе!
          </Typography>
        </div>
        <RegularButton
          onClick={() => onModalCloseHandler(0)}
          className={cx(notificationCloseButton, smallHeightAdditionalMargin)}
          data-qa={DATA_QA_CONTINUE_BUTTON}
          variant="primary"
          size="l"
          color="control-accent">
          Продолжить
        </RegularButton>
      </div>
    </div>
  );
};
